/**
 * ContextualTooltip Component
 *
 * One-time educational tooltip that points to a target element.
 * Portal-rendered to document.body (same pattern as BottomSheet).
 * z-[65] -- above header (z-50) and BottomSheet (z-[60]), below WelcomeScreen (z-[70]).
 *
 * Features:
 * - Auto-positioning above/below target via getBoundingClientRect
 * - 8px CSS triangle pointer
 * - 600ms appearance delay (lets page animations settle)
 * - Dismisses on OK button, backdrop tap, or Escape key
 * - localStorage persistence via useTooltip hook
 * - Resize/scroll repositioning with cleanup
 * - Reduced motion support via existing CSS override on fadeInUp
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../lib/utils';
import { useTooltip } from '../hooks/useTooltip';

interface ContextualTooltipProps {
  /** Unique ID for persistence */
  tooltipId: string;
  /** Ref to the DOM element the tooltip points to */
  targetRef: React.RefObject<HTMLElement | null>;
  /** Tooltip message text */
  message: string;
  /** Preferred position -- 'auto' measures available space */
  position?: 'above' | 'below' | 'auto';
  /** Delay in ms before showing (default: 600) */
  delay?: number;
}

interface TooltipPosition {
  top: number;
  left: number;
  triangleLeft: number;
  placement: 'above' | 'below';
}

const TOOLTIP_MAX_WIDTH = 220;
const VIEWPORT_PADDING = 12;
const TRIANGLE_SIZE = 8;

export function ContextualTooltip({
  tooltipId,
  targetRef,
  message,
  position = 'auto',
  delay = 600,
}: ContextualTooltipProps) {
  const { shouldShow, dismiss } = useTooltip(tooltipId);
  const [isReady, setIsReady] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [coords, setCoords] = useState<TooltipPosition | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const okButtonRef = useRef<HTMLButtonElement>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calculate tooltip position from target rect
  const calculatePosition = useCallback(() => {
    const target = targetRef.current;
    if (!target) return null;

    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Determine placement
    let placement: 'above' | 'below';
    if (position === 'auto') {
      // Pick whichever side has more room (160px threshold for tooltip + triangle)
      placement = rect.top > 160 ? 'above' : 'below';
    } else {
      placement = position;
    }

    // Measure tooltip height (approximate if not yet rendered)
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 80;

    // Vertical position
    let top: number;
    if (placement === 'above') {
      top = rect.top - tooltipHeight - TRIANGLE_SIZE;
    } else {
      top = rect.bottom + TRIANGLE_SIZE;
    }

    // Horizontal: center on target, clamp to viewport
    const targetCenterX = rect.left + rect.width / 2;
    let left = targetCenterX - TOOLTIP_MAX_WIDTH / 2;

    // Clamp to viewport bounds
    left = Math.max(VIEWPORT_PADDING, left);
    left = Math.min(viewportWidth - TOOLTIP_MAX_WIDTH - VIEWPORT_PADDING, left);

    // Triangle horizontal position (center of target, clamped within tooltip)
    const triangleLeft = Math.max(
      12,
      Math.min(targetCenterX - left, TOOLTIP_MAX_WIDTH - 12),
    );

    return { top, left, triangleLeft, placement };
  }, [targetRef, position]);

  // Recalculate position
  const recalculate = useCallback(() => {
    const pos = calculatePosition();
    if (pos) setCoords(pos);
  }, [calculatePosition]);

  // Delayed appearance + scroll target into view
  useEffect(() => {
    if (!shouldShow) return;

    const timer = setTimeout(() => {
      const target = targetRef.current;
      if (!target) return;

      // Scroll target into view if needed
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });

      // Calculate position after scroll settles
      requestAnimationFrame(() => {
        const pos = calculatePosition();
        if (pos) {
          setCoords(pos);
          setIsReady(true);
        }
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [shouldShow, delay, targetRef, calculatePosition]);

  // Focus OK button when tooltip becomes visible
  useEffect(() => {
    if (isReady && okButtonRef.current) {
      okButtonRef.current.focus();
    }
  }, [isReady]);

  // Dismiss with fade-out animation
  const handleDismiss = useCallback(() => {
    setIsDismissing(true);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => {
      dismiss();
    }, 150);
  }, [dismiss]);

  // Reposition on resize and scroll
  useEffect(() => {
    if (!isReady) return;

    window.addEventListener('resize', recalculate);
    window.addEventListener('scroll', recalculate, { passive: true });

    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('scroll', recalculate);
    };
  }, [isReady, recalculate]);

  // Escape key dismissal
  useEffect(() => {
    if (!isReady) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isReady, handleDismiss]);

  // Flush dismiss timer on unmount
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  // Don't render if already seen, not ready, or no target
  if (!shouldShow || !isReady || !coords) return null;

  const tooltip = (
    <>
      {/* Backdrop overlay */}
      <div
        className={cn(
          'fixed inset-0 z-[65] bg-black/20',
          'transition-opacity duration-150',
          isDismissing ? 'opacity-0' : 'opacity-100',
        )}
        onClick={handleDismiss}
        aria-hidden="true"
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-label="Tip"
        className={cn(
          'fixed z-[65] max-w-[220px] rounded-xl bg-op-card p-3 shadow-md',
          'transition-opacity duration-150',
          isDismissing ? 'opacity-0' : 'opacity-100',
        )}
        style={{
          top: coords.top,
          left: coords.left,
          animation: isDismissing ? undefined : 'fadeInUp 0.4s ease-out both',
        }}
      >
        {/* Triangle pointer */}
        {coords.placement === 'above' ? (
          // Triangle below tooltip, pointing down to target
          <div
            className="absolute"
            style={{
              bottom: -TRIANGLE_SIZE,
              left: coords.triangleLeft,
              width: 0,
              height: 0,
              borderLeft: `${TRIANGLE_SIZE}px solid transparent`,
              borderRight: `${TRIANGLE_SIZE}px solid transparent`,
              borderTop: `${TRIANGLE_SIZE}px solid hsl(var(--op-card))`,
              transform: 'translateX(-50%)',
            }}
          />
        ) : (
          // Triangle above tooltip, pointing up to target
          <div
            className="absolute"
            style={{
              top: -TRIANGLE_SIZE,
              left: coords.triangleLeft,
              width: 0,
              height: 0,
              borderLeft: `${TRIANGLE_SIZE}px solid transparent`,
              borderRight: `${TRIANGLE_SIZE}px solid transparent`,
              borderBottom: `${TRIANGLE_SIZE}px solid hsl(var(--op-card))`,
              transform: 'translateX(-50%)',
            }}
          />
        )}

        {/* Message */}
        <p className="text-[13px] leading-5 text-op-text-secondary">
          {message}
        </p>

        {/* OK button */}
        <button
          ref={okButtonRef}
          type="button"
          onClick={handleDismiss}
          className={cn(
            'mt-2.5 min-h-[44px] w-full rounded-lg',
            'bg-guild-green px-4 py-1.5 text-[13px] font-semibold text-white',
            'active:bg-guild-green/90 transition-colors duration-150',
            'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
          )}
        >
          OK
        </button>
      </div>
    </>
  );

  return createPortal(tooltip, document.body);
}
